import { ColumnSizingState } from '@tanstack/react-table';
import { PointerEvent as ReactPointerEvent } from 'react';

import { COLUMN_ID_ATTRIBUTE } from '../config';
import { isRTL, resizeColumnPair } from '../lib';

type UseColumnPairResizeOptions = {
  minSizes: ColumnSizingState;
  onCommit: (widths: ColumnSizingState) => void;
};

const getColumnId = (header: HTMLTableCellElement) =>
  header.getAttribute(COLUMN_ID_ATTRIBUTE) as string;

// Drags the boundary between two adjacent header cells. With
// `table-layout: fixed` the header row defines the widths, so every visible
// header is pinned to its measured pixel width for the duration of the drag
// and only the pair changes: the boundary follows the cursor 1:1 and the
// table's total width stays constant. The DOM is written directly because
// the virtualized body mounts every loaded row and would re-render per move.
export const useColumnPairResize = ({
  minSizes,
  onCommit,
}: UseColumnPairResizeOptions) => {
  const startResize = (
    event: ReactPointerEvent<HTMLElement>,
    leftId: string,
    rightId: string,
  ) => {
    if (event.button !== 0) {
      return;
    }

    const handle = event.currentTarget;
    const row = handle.closest('tr');

    if (!row) {
      return;
    }

    const headers = Array.from(
      row.querySelectorAll<HTMLTableCellElement>(`th[${COLUMN_ID_ATTRIBUTE}]`),
    ).filter((header) => getComputedStyle(header).display !== 'none');

    const leftHeader = headers.find((header) => getColumnId(header) === leftId);
    const rightHeader = headers.find(
      (header) => getColumnId(header) === rightId,
    );

    if (!leftHeader || !rightHeader) {
      return;
    }

    const startWidths: ColumnSizingState = Object.fromEntries(
      headers.map((header) => [getColumnId(header), header.offsetWidth]),
    );
    const originalStyles = headers.map((header) => header.style.width);

    const direction = isRTL(handle) ? -1 : 1;
    const startX = event.clientX;

    const widthsAt = (clientX: number) =>
      resizeColumnPair(
        startWidths,
        minSizes,
        leftId,
        rightId,
        (clientX - startX) * direction,
      );

    const applyWidths = (widths: ColumnSizingState) => {
      headers.forEach((header) => {
        header.style.width = `${widths[getColumnId(header)]}px`;
      });
    };

    const restoreStyles = () => {
      headers.forEach((header, index) => {
        header.style.width = originalStyles[index];
      });
    };

    // jsdom has no pointer capture; the browser needs it so a fast drag
    // keeps reporting to the handle after the cursor leaves it.
    handle.setPointerCapture?.(event.pointerId);
    event.preventDefault();

    const handleMove = (moveEvent: PointerEvent) => {
      applyWidths(widthsAt(moveEvent.clientX));
    };

    const stopListening = (pointerId: number) => {
      handle.removeEventListener('pointermove', handleMove);
      handle.removeEventListener('pointerup', finish);
      handle.removeEventListener('pointercancel', cancel);
      handle.removeEventListener('lostpointercapture', cancel);
      handle.releasePointerCapture?.(pointerId);
    };

    const finish = (upEvent: PointerEvent) => {
      stopListening(upEvent.pointerId);
      // Restore first: the committed sizes come back through props as
      // percentages, and React skips a style it believes is unchanged.
      restoreStyles();

      if (upEvent.clientX !== startX) {
        onCommit(widthsAt(upEvent.clientX));
      }
    };

    const cancel = (cancelEvent: PointerEvent) => {
      stopListening(cancelEvent.pointerId);
      restoreStyles();
    };

    applyWidths(startWidths);
    handle.addEventListener('pointermove', handleMove);
    handle.addEventListener('pointerup', finish);
    handle.addEventListener('pointercancel', cancel);
    handle.addEventListener('lostpointercapture', cancel);
  };

  return { startResize };
};
